import "./InputDateTime.scss"
import iconClearIndicator from "assets/img/icon-clear-indicator.svg"

import { FC, KeyboardEvent, MouseEvent, useMemo } from "react"
import { useTranslation } from "react-i18next"
import dayjs, { Dayjs } from "utils/dayjs"
import cx from "classnames"

import Input from "components/Input/Input"
import i18n from "intl/i18n"
import config from "config/config"
import toastMessages from "stores/toastMessages"
import { toDateTimeWithoutTimezone } from "utils/utils"
import DateTimeControl, { IDateTimeControlProps } from "components/DateTimeControl/DateTimeControl"
import { GroupedControl } from "config/constants"

interface IInputDateTimeProps extends Omit<IDateTimeControlProps, "children"> {
    placeholder?: string
    isInvalid?: boolean
    disabled?: boolean
    dateFormat?: string
    clearButton?: boolean
    calendarIcon?: boolean
    grouped?: GroupedControl
}

const InputDateTime: FC<IInputDateTimeProps> = ({
    placeholder = i18n.t('common.pleaseSelect'),
    isInvalid = false,
    disabled = false,
    dateFormat: dateFormatProp,
    clearButton,
    calendarIcon = true,
    grouped,

    // DateTimeControl props
    type = "date",
    date = null,
    utc,
    onDateChange = () => { },
    testIdPrefix = "input-date-time",
    ...otherDateTimeControlProps
}) => {
    const { t } = useTranslation()
    const dateFormat = dateFormatProp || (type === "datetime" && t("config.dateLongTimeFormat")) || t("config.dateLongFormat")
    const inpValue = useMemo(() => {
        if (!date || !date.isValid()) return ""
        return (utc ? dayjs(toDateTimeWithoutTimezone(date)).utc() : date.clone().local())
            .locale(i18n.language || config.defaultLanguage).format(dateFormat) || ""
    }, [dateFormat, date, utc])
    const shouldShowClearButton = clearButton && !disabled && !!inpValue

    async function handleDateChange(dateToApply: Dayjs | null) {
        try {
            await onDateChange(dateToApply)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }

    function handleInputKeyPress(e: KeyboardEvent, openDateTimeModal: () => void) {
        e.stopPropagation()
        e.preventDefault()
        if ((e.key !== "Enter" && e.key !== " ") || disabled) return
        openDateTimeModal()
    }

    function onClearClick(e: MouseEvent) {
        e.stopPropagation()
        handleDateChange(null)
    }

    return (
        <DateTimeControl
            type={type}
            date={date}
            utc={utc}
            testIdPrefix={testIdPrefix}
            onDateChange={handleDateChange}
            {...otherDateTimeControlProps}>
            {({ openDateTimeModal }) => (
                <>
                    <div className={cx([
                        "input-date-time",
                        {
                            "input-date-time--disabled": disabled,
                            "input-date-time--with-clear-button": shouldShowClearButton,
                            "input-date-time--with-icon": calendarIcon,
                        }
                    ])}>
                        <div className="input-date-time__input-wrap" onClick={disabled ? undefined : openDateTimeModal}>
                            <Input
                                type="text"
                                readOnly
                                placeholder={placeholder}
                                value={inpValue}
                                disabled={disabled}
                                grouped={grouped}
                                isInvalid={isInvalid}
                                onKeyPress={e => handleInputKeyPress(e, openDateTimeModal)}
                                after={
                                    <>
                                        {shouldShowClearButton &&
                                            <button
                                                type="button"
                                                className="input-date-time__reset"
                                                aria-label="Reset"
                                                onClick={onClearClick}>
                                                <img src={iconClearIndicator} alt="" />
                                            </button>
                                        }
                                        {calendarIcon &&
                                            <div className="input-date-time__icon"></div>
                                        }
                                    </>
                                }
                                data-testid={`${testIdPrefix}-input`}
                            />
                        </div>
                    </div>
                </>
            )}
        </DateTimeControl >
    )
}

export default InputDateTime
